import { useMutation, useQuery, type QueryKey } from '@tanstack/react-query';
import { useDebounce } from '@odo/shared-ui';
import {
    createDmDoctor,
    createDmPractice,
    searchDmDoctors,
    searchDmPractices,
    type CreateDmDoctorBody,
    type CreateDmPracticeBody,
    type DmDoctorOption,
    type DmPracticeOption
} from '../services/practiceDm';
import { PRACTICE_DM_DOCTOR_SEARCH_KEY, PRACTICE_DM_PRACTICE_SEARCH_KEY } from './queryKeys';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

export interface DmSearchState<T> {
    results: T[];
    /** False until the typed term is long enough for the backend to accept it. */
    isActive: boolean;
    isSearching: boolean;
    error: unknown;
}

// TanStack aborts the signal once the debounced term moves on, cancelling the stale request.
function useDmSearch<T>(
    search: string,
    keyFor: (term: string) => QueryKey,
    fetcher: (term: string, signal: AbortSignal) => Promise<T[]>
): DmSearchState<T> {
    const trimmed = search.trim();
    const term = useDebounce(trimmed, SEARCH_DEBOUNCE_MS);
    const isActive = trimmed.length >= SEARCH_MIN_CHARS;
    const query = useQuery({
        queryKey: keyFor(term),
        queryFn: ({ signal }) => fetcher(term, signal),
        enabled: term.length >= SEARCH_MIN_CHARS,
        retry: false
    });
    const isDebouncing = isActive && trimmed !== term;

    return {
        results: query.data ?? [],
        isActive,
        isSearching: isDebouncing || query.isFetching,
        error: isDebouncing ? null : query.error
    };
}

export function useDmDoctorSearch(practiceId: string, search: string) {
    return useDmSearch<DmDoctorOption>(
        search,
        (term) => PRACTICE_DM_DOCTOR_SEARCH_KEY(practiceId, term),
        (term, signal) => searchDmDoctors(practiceId, term, signal)
    );
}

export function useDmPracticeSearch(practiceId: string, search: string) {
    return useDmSearch<DmPracticeOption>(
        search,
        (term) => PRACTICE_DM_PRACTICE_SEARCH_KEY(practiceId, term),
        (term, signal) => searchDmPractices(practiceId, term, signal)
    );
}

export function useCreateDmPractice(practiceId: string) {
    return useMutation({
        mutationFn: (body: CreateDmPracticeBody) => createDmPractice(practiceId, body)
    });
}

export function useCreateDmDoctor(practiceId: string) {
    return useMutation({
        mutationFn: (body: CreateDmDoctorBody) => createDmDoctor(practiceId, body)
    });
}
